import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  AddOutlined as AddOutlinedIcon,
  ArrowOutwardOutlined as ArrowOutwardOutlinedIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Diversity3Outlined as Diversity3OutlinedIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
  HighlightOffRounded as HighlightOffRoundedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  MoreVert as MoreVertIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  Search as SearchOutlinedIcon,
} from "../../../../components/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import Loader from "../../../../components/Loader";
import {
  assignDriver,
  fetchAllDrivers,
  getFleetBranches,
  getDriverHistory,
  getDriverAssignments,
  removeDriver,
} from "../../../../services/dashboard";
import { useToast } from "../../../../hooks/useToast";
import VerifiedPopup from "../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../store/useVerifiedStore";
import { useDriverStore } from "../../../../store/useDriverStore";
import ConfirmDeletePopup from "../../../../components/ConfirmDeletePopup";
import AssignDriverPopup from "../popups/AssignDriverPopup";
import { useAuthStore } from "../../../../store/useAuthStore";
import History from "../../claims/common/History";
import { useSessionStore } from "../../../../store/useSessionStore";
import {
  OperationsEmptyState,
  OperationsPageShell,
  formatOperationsStatus,
  ifasColors,
} from "../../ifas/components/OperationsUI";
import {
  STANDARD_TONES,
  StandardCountBadge,
  StandardMetricCard,
  StandardPager,
  StatusDotChip,
  Tone,
  operationsStatusTone,
  standardGhostBtnSx,
  standardPrimaryBtnSx,
  standardTableHeadCellSx,
  standardTableRowSx,
} from "../../ifas/components/StandardUI";

// The full DriverAvailabilityStatus enum (backend:
// com.iceclaim.enums.DriverAvailabilityStatus) — used for the status filter
// options and so the KPI buckets sum to the total (F.2 AC1/AC2).
const DRIVER_STATUS_OPTIONS = [
  "AVAILABLE",
  "ASSIGNED",
  "DISPATCHED",
  "IN_SERVICE",
  "UNAVAILABLE",
  "TERMINATED",
  "REMOVED",
];

const DashCell = () => (
  <Typography component="span" sx={{ color: ifasColors.muted, fontWeight: 700 }}>
    —
  </Typography>
);

const Drivers = ({ title, recentFilter, onSelectDriver, driverOnly = false, initialCaseReference = "", initialAssignmentPurpose = "", initialRequestReference = "", initialAllocationRole = "", initialAllocationSequence = null, focusDriverNumber = "", onAssignmentCompleted, onAddDriverClick }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [driversItems, setDriversItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [openAssignPopup, setOpenAssignPopup] = useState(false);
  const rowsPerPage = 10;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popupLabel, setPopupLabel] = useState("");
  const [driverStatus, setDriverStatus] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [myAssignments, setMyAssignments] = useState<any[]>([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [fleetBranches, setFleetBranches] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Tier 2/900px toolbar: branch+status collapse behind this menu anchor.
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  // Tier 3 card list: which driverID's "More details" panel is open.
  const [expandedDriverIds, setExpandedDriverIds] = useState<Set<string>>(new Set());
  // Card tier's primary "Release" button sets selectedItem and, in the same
  // click, needs handleRemoveDriver() to see it — but handleRemoveDriver
  // reads selectedItem from state, which is not yet flushed synchronously
  // after setSelectedItem in the same handler. This flag defers the
  // (unmodified) handleRemoveDriver() call to the effect below, once
  // selectedItem has actually updated. "Assign" doesn't need this: its
  // handler (handleAssignDriver) takes the item directly as an argument.
  const [pendingRelease, setPendingRelease] = useState(false);
  const { showVerified, setShowVerified } = useVerifiedStore();
  const { first_name, last_name, email, user_name, userId, driverId } = useAuthStore();
  const { userName, userIdData } = useSessionStore();
  // 7-key literal (F.2 AC2) — widened from the previous 5-key shape so the
  // KPI row (AC1) can tile all 7 DriverAvailabilityStatus values (Exited
  // merges TERMINATED + REMOVED) and the buckets sum to the total.
  const [statusCounts, setStatusCounts] = useState({
    AVAILABLE: 0,
    ASSIGNED: 0,
    IN_SERVICE: 0,
    DISPATCHED: 0,
    UNAVAILABLE: 0,
    TERMINATED: 0,
    REMOVED: 0,
  });

  const { showInfoToast } = useToast();

  const branchCaption = `Across ${fleetBranches.length} branch${fleetBranches.length === 1 ? "" : "es"}`;
  const exitedCount = (statusCounts.TERMINATED || 0) + (statusCounts.REMOVED || 0);

  const metrics: Array<{ label: string; value: number; caption: string; tone: Tone; icon: React.ReactNode }> = [
    {
      label: "Total Drivers",
      value: totalElements || 0,
      caption: branchCaption,
      tone: "grey",
      icon: <Diversity3OutlinedIcon />,
    },
    {
      label: "Available",
      value: statusCounts.AVAILABLE || 0,
      caption: "Ready to assign",
      tone: "green",
      icon: <CheckCircleOutlineIcon />,
    },
    {
      label: "Assigned",
      value: statusCounts.ASSIGNED || 0,
      caption: "On active cases",
      tone: "purple",
      icon: <AssignmentTurnedInOutlinedIcon />,
    },
    {
      label: "Dispatched",
      value: statusCounts.DISPATCHED || 0,
      caption: "Out on the road",
      tone: "orange",
      icon: <LocalShippingOutlinedIcon />,
    },
    {
      label: "In Service",
      value: statusCounts.IN_SERVICE || 0,
      caption: "Currently on duty",
      tone: "blue",
      icon: <AccessTimeOutlinedIcon />,
    },
    {
      label: "Unavailable",
      value: statusCounts.UNAVAILABLE || 0,
      caption: "Off rotation",
      tone: "grey",
      icon: <HighlightOffRoundedIcon />,
    },
    {
      label: "Exited",
      value: exitedCount,
      caption: "Terminated or removed",
      tone: "red",
      icon: <ArrowOutwardOutlinedIcon />,
    },
  ];

  const driverColumns = [
    "Driver ID",
    "Full Name",
    "Mobile",
    "Branch",
    "Licence Code",
    "PDP",
    "Licence Expiry",
    "Assigned Vehicle",
    "Current Work",
    "Status",
    "Actions",
  ];

  // Tier 2 (600px–1279px) condensed table — same 11 fields, folded into 6
  // columns; the dropped columns become a secondary line inside the cell
  // that keeps them (see DRIVER_CARD/condensed row render below), so no
  // information is dropped, only re-laid-out.
  const condensedColumns = ["Driver", "Contact", "Vehicle", "Current Work", "Status", "Actions"];

  // Explicit media query strings — the app's theme has no breakpoint
  // overrides, so stock MUI md=900/lg=1200 do not line up with the 1280
  // desktop-freeze target. isDesktopTable/isCardList decide which tier
  // mounts; everything in between (600–1279) is the condensed table.
  const isDesktopTable = useMediaQuery("(min-width:1280px)");
  const isCardList = useMediaQuery("(max-width:599px)");
  // Toolbar-only cutoff (Bravo BLOCKER #1): inline branch/status selects
  // above 900px, a single "Filters" menu below it. Independent of the
  // table/card tier decision above.
  const isFilterInline = useMediaQuery("(min-width:900px)");

  const columns = isDesktopTable ? driverColumns : condensedColumns;

  const normalizeDriverIdentity = (value: any) =>
    String(value || "").trim().toLowerCase();

  const currentDriverKeys = [
    driverId,
    userId,
    userIdData,
    email,
    userName,
    user_name,
    first_name,
    last_name,
    `${first_name || ""} ${last_name || ""}`.trim(),
  ]
    .map(normalizeDriverIdentity)
    .filter(Boolean);

  const isCurrentDriver = (driver: any) => {
    if (!currentDriverKeys.length) return false;

    const normalizedAuthDriverId = normalizeDriverIdentity(driverId);
    const normalizedRecordDriverId = normalizeDriverIdentity(
      driver?.driverID || driver?.driverId || driver?.id,
    );

    if (normalizedAuthDriverId && normalizedRecordDriverId) {
      return normalizedAuthDriverId === normalizedRecordDriverId;
    }

    const driverKeys = [
      driver?.driverID,
      driver?.driverId,
      driver?.id,
      driver?.userId,
      driver?.userID,
      driver?.email,
      driver?.driverEmail,
      driver?.userEmail,
      driver?.userName,
      driver?.username,
      driver?.firstName,
      driver?.lastName,
      `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim(),
    ]
      .map(normalizeDriverIdentity)
      .filter(Boolean);

    return driverKeys.some((key) => currentDriverKeys.includes(key));
  };

  const getDriverStatusCounts = (items: any[]) =>
    items.reduce(
      (counts, item) => {
        const status = String(item?.driverStatus || "").toUpperCase();
        if (status && status in counts) {
          counts[status as keyof typeof counts] += 1;
        }
        return counts;
      },
      {
        AVAILABLE: 0,
        ASSIGNED: 0,
        IN_SERVICE: 0,
        DISPATCHED: 0,
        UNAVAILABLE: 0,
        TERMINATED: 0,
        REMOVED: 0,
      },
    );

  const fetchAllDriversList = async (page = 0) => {
    try {
      setIsLoading(true);

      const response = await fetchAllDrivers(
        driverOnly ? 0 : page,
        driverOnly ? 1000 : rowsPerPage,
        undefined,
        !driverOnly ? branchFilter || undefined : undefined,
      );

      if (response.status === 200) {
        const driverList = response.data.drivers || response.data.driver || [];
        const drivers = Array.isArray(driverList)
          ? driverList
          : [driverList].filter(Boolean);
        const visibleDrivers = driverOnly
          ? drivers.filter(isCurrentDriver)
          : drivers;

        setDriversItems(visibleDrivers);
        setStatusCounts(
          driverOnly ? getDriverStatusCounts(visibleDrivers) : response.data.statusCounts,
        );
        setTotalElements(driverOnly ? visibleDrivers.length : response.data.totalCount);
        setLoadError(false);
      } else {
        setLoadError(true);
        showInfoToast(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching vehicles", error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const currentDriverId = driversItems[0]?.driverID;
    if (!driverOnly || !currentDriverId) {
      setMyAssignments([]);
      return;
    }
    let active = true;
    const loadAssignments = async () => {
      const response = await getDriverAssignments(currentDriverId);
      if (active && response?.status === 200) {
        setMyAssignments(Array.isArray(response.data) ? response.data : []);
      }
    };
    void loadAssignments();
    const timer = window.setInterval(() => void loadAssignments(), 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [driverOnly, driversItems]);

  // Free-text search and status filter run over the currently loaded page —
  // fetchAllDrivers has no search/status parameter, and F.2 AC4 requires no
  // new fetch signature. Branch filtering stays server-side via the existing
  // hook. Hidden entirely for driverOnly (AC9): a one-record view has nothing
  // to filter.
  const visibleItems = useMemo(() => {
    if (driverOnly) return driversItems;

    const query = searchText.trim().toLowerCase();

    return driversItems.filter((item: any) => {
      if (statusFilter && String(item?.driverStatus || "").toUpperCase() !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const haystack = [item.driverNumber, item.firstName, item.lastName, item.phoneNumber]
        .map((v: any) => String(v || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [driversItems, searchText, statusFilter, driverOnly]);

  const activeFilterCount = (branchFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  // Shared by the condensed-table kebab and the card-list kebab/primary
  // action so both replicate exactly what the desktop kebab does today:
  // stamp selectedItem + driverStatus first, optionally opening the menu.
  const openActionsForItem = (item: any, anchor?: HTMLElement) => {
    setSelectedItem(item);
    setDriverStatus(item?.driverStatus);
    if (anchor) setAnchorEl(anchor);
  };

  const toggleCardExpanded = (id: string) => {
    setExpandedDriverIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAssignDriver = (item: any) => {
    if (!item?.driverID) {
      showInfoToast("Unable to identify the selected driver");
      return;
    }

    setSelectedItem(item);
    setOpenAssignPopup(true);
  };

  const handleAssign = async ({
    vehicleID,
    caseReference,
  }: {
    vehicleID: string;
    caseReference: string;
  }) => {
    if (!selectedItem?.driverID) {
      showInfoToast("Unable to identify the selected driver");
      return;
    }

    if (!vehicleID) {
      showInfoToast("Please select a vehicle");
      return;
    }

    if (!caseReference.trim()) {
      showInfoToast("Please enter case reference");
      return;
    }

    try {
      setIsLoading(true);
      const response = await assignDriver(selectedItem.driverID, {
        vehicleID,
        caseReference: caseReference.trim(),
        assignmentPurpose: initialAssignmentPurpose || undefined,
        requestReference: initialRequestReference || undefined,
        allocationRole: initialAllocationRole || undefined,
        allocationSequence: initialAllocationSequence || undefined,
        assignedBy: `${first_name || ""} ${last_name || ""}`.trim()
          || user_name
          || userName
          || email
          || "Fleet Coordinator",
      });

      if (response.status === 200 || response.status === 201) {
        setPopupLabel("Assigned");
        setShowVerified(true);
        setOpenAssignPopup(false);
        setAnchorEl(null);
        setSelectedItem(null);
        onAssignmentCompleted?.();
        await fetchAllDriversList(currentPage);
      } else {
        showInfoToast(response?.data?.message || "Failed to assign driver");
      }
    } catch (error) {
      console.log(error);
      showInfoToast("Failed to assign driver");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveDriver = () => {
    if (!selectedItem?.driverID) {
      showInfoToast("Unable to identify the selected driver");
      return;
    }

    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem?.driverID) {
      showInfoToast("Unable to identify the selected driver");
      setShowConfirmDelete(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await removeDriver(
        selectedItem.driverID,
        {
          status: "AVAILABLE",
          reason: "Driver removed from current assignment",
        },
      );

      if (data.status === 200 || data.status === 201) {
        setPopupLabel("Released");
        setShowVerified(true);
        setShowConfirmDelete(false);
        setAnchorEl(null);
        setSelectedItem(null);
        await fetchAllDriversList(currentPage);
      } else {
        setShowConfirmDelete(false);
        showInfoToast(data.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfirmPopup = () => {
    setShowConfirmDelete(false);
    setSelectedItem(null);
  };

  // See pendingRelease declaration above: fires the unmodified
  // handleRemoveDriver() only once selectedItem has actually flushed.
  useEffect(() => {
    if (pendingRelease && selectedItem) {
      handleRemoveDriver();
      setPendingRelease(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRelease, selectedItem]);

  useEffect(() => {
    fetchAllDriversList(currentPage);
    // fetchAllDriversList depends on derived identity keys and should only refetch on page/role mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, driverOnly, branchFilter]);

  useEffect(() => {
    if (driverOnly) {
      return;
    }

    const loadFleetBranches = async () => {
      const response = await getFleetBranches();
      if (response?.status === 200) {
        const branches = Array.isArray(response.data) ? response.data : [];
        setFleetBranches(
          [...branches].sort((a: any, b: any) =>
            String(a.branchName || "").localeCompare(String(b.branchName || "")),
          ),
        );
      }
    };

    void loadFleetBranches();
  }, [driverOnly]);

  // HTMLElement, not HTMLTableRowElement: the same ref now has to be able to
  // land on either a <TableRow> (desktop/condensed tiers) or a card <Box>
  // (card tier) — only one of which is ever mounted at a given width.
  const focusedRowRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!focusDriverNumber || !driversItems.length) {
      return;
    }

    focusedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusDriverNumber, driversItems]);

  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        setShowVerified(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showVerified]);

  const handleHistoryClick = async () => {
    if (!selectedItem?.driverID) {
      showInfoToast("Unable to identify the selected driver");
      return;
    }

    setAnchorEl(null);
    setIsLoading(true);
    const res = await getDriverHistory(selectedItem.driverID);
    if (res?.status === 200) {
      useDriverStore.getState().setSelectedDriver(selectedItem);
      setHistoryData(res.data);
      setShowHistory(true);
    } else {
      showInfoToast("Failed to load driver history. Please try again.");
    }
    setIsLoading(false);
  };

  const handleBackFromHistory = () => {
    setShowHistory(false);
  };

  const handleOpenDetails = (item: any) => {
    useDriverStore.getState().setSelectedDriver(item);
    onSelectDriver(item.driverID);
  };

  if (showHistory) {
    return (
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <History
          onBack={handleBackFromHistory}
          historyData={historyData}
          isLoading={isLoading}
          historyType="case"
        />
      </Box>
    );
  }

  const branchFilterLabel = branchFilter
    ? fleetBranches.find((branch: any) => branch.id === branchFilter)?.branchName || "Selected"
    : "All";
  const statusFilterLabel = statusFilter ? formatOperationsStatus(statusFilter) : "All";

  return (
    <OperationsPageShell
      eyebrow="Fleet Management"
      title={driverOnly ? "Driver" : title}
      description={
        driverOnly
          ? "View your driver record, assigned vehicle, licence status, and history in one place."
          : "The fleet driver register — availability, licence and assignment status across all branches. Open a driver for their profile, documents and history."
      }
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => fetchAllDriversList(currentPage)}
            sx={standardGhostBtnSx}
          >
            Refresh
          </Button>
          {!driverOnly && (
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => onAddDriverClick?.()}
              sx={standardPrimaryBtnSx}
            >
              Add Driver
            </Button>
          )}
        </Stack>
      }
    >
      {isLoading && <Loader isLoading={isLoading} />}

      {driverOnly && (
        <Box mt={2.5} mb={2.5}>
          <Typography fontSize={18} fontWeight={900} mb={1.5}>My Assignments ({myAssignments.length} active)</Typography>
          {myAssignments.length === 0 ? (
            <Paper elevation={0} sx={{ p: 2, border: "1px solid #EAECF0", borderRadius: 2 }}>
              No active duties assigned.
            </Paper>
          ) : (
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }} gap={2}>
              {myAssignments.map((duty: any) => (
                <Paper key={duty.allocationId || duty.bodyRemovalNumber} elevation={0} sx={{ p: 2.25, border: "1px solid #EAECF0", borderRadius: 2 }}>
                  <Typography fontWeight={800} fontSize={16}>
                    {formatOperationsStatus(duty.assignmentPurpose || "Duty")}
                    {duty.allocationRole ? ` · ${formatOperationsStatus(duty.allocationRole)}` : ""}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary" mb={1.5}>
                    {duty.allocationNumber || duty.bodyRemovalNumber} · Case {duty.caseNumber}
                    {duty.requestReference ? ` · Request ${duty.requestReference}` : ""}
                  </Typography>
                  <Stack spacing={0.75} fontSize={13}>
                    <Box><b>When:</b> {duty.scheduledDate || "To be confirmed"} {duty.scheduledTime || ""}</Box>
                    <Box><b>Vehicle:</b> {duty.vehicleNumber || "To be confirmed"}</Box>
                    <Box><b>Pickup:</b> {duty.pickupLocation || "Pickup location to be confirmed"}</Box>
                    <Box><b>Destination:</b> {duty.destinationLocation || "Destination to be confirmed"}</Box>
                    {(duty.contactName || duty.contactNumber) && <Box><b>Contact:</b> {duty.contactName || ""} {duty.contactNumber || ""}</Box>}
                    {duty.instructions && <Box sx={{ whiteSpace: "pre-wrap" }}><b>Instructions:</b> {duty.instructions}</Box>}
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* KPI row is a fix, not current behaviour (F.2 AC9): a driverOnly
          self-service driver used to get fleet-wide tiles computed over their
          own single record. A one-record view has no meaning for fleet KPIs,
          so it is hidden entirely rather than shown wrong. */}
      {!driverOnly && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              // 2-up at handheld widths so the 7-tile KPI row reads as one
              // compact block instead of a 7-screen-long single column
              // before the register even starts. sm/md/lg unchanged.
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: "repeat(7, minmax(0, 1fr))",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {metrics.map((metric) => (
            <StandardMetricCard
              key={metric.label}
              label={metric.label}
              value={metric.value}
              caption={metric.caption}
              tone={metric.tone}
              icon={metric.icon}
              compact
            />
          ))}
        </Box>
      )}

      {!driverOnly && (
        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            mb: 2,
            border: `1px solid ${ifasColors.border}`,
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${ifasColors.softOrange}, ${ifasColors.surface} 46%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: ifasColors.softOrange,
                  color: ifasColors.orange,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Diversity3OutlinedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 19, fontWeight: 900, color: ifasColors.text }}>
                  Driver Register
                </Typography>
                <Typography sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 12.5, lineHeight: 1.45, maxWidth: 520 }}>
                  Search and filter drivers. Actions stay on each row.
                </Typography>
              </Box>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: 0, width: { xs: "100%", md: "auto" } }} flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search name, ID, mobile…"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
                sx={{
                  // Below 900px the search field goes full width with no
                  // floor; at 900px+ it keeps the original 220px minimum
                  // (the "lg" bucket at 1200px+ matches the 1280 freeze
                  // target exactly, so nothing visibly changes there).
                  width: { xs: "100%", md: "auto" },
                  minWidth: { xs: 0, md: 180, lg: 220 },
                  bgcolor: ifasColors.surface,
                  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                }}
              />
              {isFilterInline ? (
                <>
                  <TextField
                    select
                    size="small"
                    value={branchFilter}
                    onChange={(event) => {
                      setCurrentPage(0);
                      setBranchFilter(event.target.value);
                    }}
                    SelectProps={{ displayEmpty: true }}
                    sx={{ minWidth: { md: 160, lg: 190 }, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  >
                    <MenuItem value="">All branches</MenuItem>
                    {fleetBranches.map((branch: any) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.branchName}
                        {branch.province ? ` - ${branch.province}` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    sx={{ minWidth: { md: 160, lg: 190 }, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    {DRIVER_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {formatOperationsStatus(option)}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={(event) => setFilterMenuAnchor(event.currentTarget)}
                  sx={{ ...standardGhostBtnSx, minHeight: 44, width: { xs: "100%", sm: "auto" } }}
                >
                  {activeFilterCount ? `Filters · ${activeFilterCount}` : "Filters"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Branch + status live here below 900px — same state, same handlers,
          just reachable through a menu instead of being clipped by
          OperationsPageShell's overflowX:hidden (Bravo BLOCKER #1). Anchored
          to the button's right edge so it can never render off-screen. */}
      {!driverOnly && (
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { p: 1.5, width: 260, maxWidth: "calc(100vw - 32px)" } }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", color: ifasColors.muted, px: 0.5, pb: 0.5 }}>
            Branch
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={branchFilter}
            onChange={(event) => {
              setCurrentPage(0);
              setBranchFilter(event.target.value);
            }}
            SelectProps={{ displayEmpty: true }}
            sx={{ mb: 1.5, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          >
            <MenuItem value="">All branches</MenuItem>
            {fleetBranches.map((branch: any) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.branchName}
                {branch.province ? ` - ${branch.province}` : ""}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", color: ifasColors.muted, px: 0.5, pb: 0.5 }}>
            Status
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {DRIVER_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {formatOperationsStatus(option)}
              </MenuItem>
            ))}
          </TextField>
        </Menu>
      )}

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: ifasColors.surface,
          boxShadow: "0 4px 14px rgba(20,24,31,.05)",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            borderBottom: `1px solid ${ifasColors.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>
              {driverOnly ? "Your Driver Record" : recentFilter || "Drivers"}
            </Typography>
            {driverOnly ? (
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: STANDARD_TONES.green.color,
                  bgcolor: STANDARD_TONES.green.bg,
                  height: 24,
                  px: 1.1,
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                Your record
              </Box>
            ) : (
              <StandardCountBadge count={totalElements || 0} />
            )}
          </Stack>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: ifasColors.muted }}>
            {driverOnly ? "No branch or status filter · no KPI row" : `Branch: ${branchFilterLabel} · Status: ${statusFilterLabel}`}
          </Typography>
        </Box>

        {isCardList ? (
          // TIER 3 (<=599px) — card list, same visibleItems, same handlers.
          // Empty/error states render directly here (not inside a table
          // cell), so Retry is on-screen and tappable at 390px.
          <Box sx={{ p: 2 }}>
            {!loadError && visibleItems.length > 0 && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.25 }}>
                {visibleItems.map((item: any) => {
                  const isFocused = Boolean(focusDriverNumber) && String(item.driverNumber) === String(focusDriverNumber);
                  const cardKey = String(item.id || item.driverID);

                  return (
                    <DriverCard
                      key={cardKey}
                      item={item}
                      driverOnly={driverOnly}
                      isFocused={isFocused}
                      expanded={expandedDriverIds.has(cardKey)}
                      onToggleExpanded={() => toggleCardExpanded(cardKey)}
                      onOpen={() => handleOpenDetails(item)}
                      onKebab={(event) => openActionsForItem(item, event.currentTarget)}
                      onPrimaryAction={() => {
                        const status = String(item?.driverStatus || "").toUpperCase();
                        openActionsForItem(item);
                        if (status === "AVAILABLE") {
                          handleAssignDriver(item);
                        } else if (["ASSIGNED", "DISPATCHED"].includes(status)) {
                          setPendingRelease(true);
                        }
                      }}
                      registerRef={(el) => {
                        focusedRowRef.current = el;
                      }}
                    />
                  );
                })}
              </Box>
            )}
            {!loadError && visibleItems.length === 0 && (
              <OperationsEmptyState
                title={
                  driverOnly
                    ? "No driver record is linked to this login yet."
                    : "No driver records found"
                }
                description={
                  driverOnly
                    ? undefined
                    : "No drivers match this branch/search. Clear filters or add a driver."
                }
              />
            )}
            {loadError && (
              <>
                <OperationsEmptyState
                  title="Couldn't load drivers"
                  description="Something went wrong fetching the register."
                />
                <Box display="flex" justifyContent="center" pb={2}>
                  <Button variant="outlined" sx={standardGhostBtnSx} onClick={() => fetchAllDriversList(currentPage)}>
                    Retry
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            {/* TIER 1 (>=1280px): minWidth 1260, 11 columns — unchanged.
                TIER 2 (600–1279px): minWidth 700, 6 columns (condensedColumns). */}
            <Table sx={{ minWidth: isDesktopTable ? 1260 : 700 }}>
              <TableHead>
                <TableRow>
                  {columns.map((col, index) => (
                    <TableCell
                      key={index}
                      align={col === "Actions" ? "right" : "left"}
                      sx={standardTableHeadCellSx}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loadError &&
                  visibleItems.map((item: any) => {
                    const isFocused = Boolean(focusDriverNumber) && String(item.driverNumber) === String(focusDriverNumber);
                    const workRef = item.currentCaseNumber || item.currentBodyRemovalNumber || item.currentAllocationNumber;
                    const workStatus = item.currentBodyRemovalStatus || item.currentAllocationStatus;
                    const rowKey = item.id || item.driverID;

                    if (isDesktopTable) {
                      // TIER 1 — byte-for-byte the original row.
                      return (
                        <TableRow
                          key={rowKey}
                          hover
                          ref={isFocused ? (focusedRowRef as React.MutableRefObject<HTMLTableRowElement | null>) : undefined}
                          sx={isFocused ? { ...standardTableRowSx, bgcolor: `${ifasColors.softOrange} !important` } : standardTableRowSx}
                          onClick={() => handleOpenDetails(item)}
                          style={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Typography fontWeight={900} color={ifasColors.text}>
                              {item.driverNumber || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                            {item.firstName} {item.lastName}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>{item.phoneNumber}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                            {item.branchName || item.branchLocation || "Unassigned"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>{item.licenseCode}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: item.hasPdp ? STANDARD_TONES.green.color : ifasColors.muted }}>
                            {item.hasPdp ? "Yes" : "No"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                            {item.licenseExpiryDate || "N/A"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                            {item.assignedVehicleNumber || item.vehicle || <DashCell />}
                          </TableCell>
                          <TableCell>
                            {workRef ? (
                              <Box display="flex" flexDirection="column" gap={0.25}>
                                <Typography fontWeight={900} color={ifasColors.text}>
                                  {workRef}
                                </Typography>
                                {workStatus && (
                                  <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                                    {formatOperationsStatus(workStatus)}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <DashCell />
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusDotChip status={item.driverStatus} tone={operationsStatusTone(item.driverStatus)} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setDriverStatus(item?.driverStatus);
                                setAnchorEl(e.currentTarget);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // TIER 2 (600px–1279px) — same 11 fields, folded into 6
                    // columns; dropped fields become a secondary line inside
                    // the cell that keeps them (Driver, Contact).
                    return (
                      <TableRow
                        key={rowKey}
                        hover
                        ref={isFocused ? (focusedRowRef as React.MutableRefObject<HTMLTableRowElement | null>) : undefined}
                        sx={isFocused ? { ...standardTableRowSx, bgcolor: `${ifasColors.softOrange} !important` } : standardTableRowSx}
                        onClick={() => handleOpenDetails(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ whiteSpace: "normal" }}>
                          <Typography fontWeight={900} color={ifasColors.text}>
                            {item.driverNumber || "N/A"}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, color: ifasColors.text, fontSize: 13, mt: 0.15 }}>
                            {item.firstName} {item.lastName}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: ifasColors.muted, mt: 0.25 }}>
                            {item.licenseCode} · PDP {item.hasPdp ? "Yes" : "No"} · Exp {item.licenseExpiryDate || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "normal" }}>
                          <Typography sx={{ fontWeight: 600, color: ifasColors.text }}>{item.phoneNumber}</Typography>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: ifasColors.muted, mt: 0.25 }}>
                            {item.branchName || item.branchLocation || "Unassigned"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                          {item.assignedVehicleNumber || item.vehicle || <DashCell />}
                        </TableCell>
                        <TableCell>
                          {workRef ? (
                            <Box display="flex" flexDirection="column" gap={0.25}>
                              <Typography fontWeight={900} color={ifasColors.text}>
                                {workRef}
                              </Typography>
                              {workStatus && (
                                <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                                  {formatOperationsStatus(workStatus)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <DashCell />
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusDotChip status={item.driverStatus} tone={operationsStatusTone(item.driverStatus)} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              openActionsForItem(item, e.currentTarget);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {!loadError && visibleItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <OperationsEmptyState
                        title={
                          driverOnly
                            ? "No driver record is linked to this login yet."
                            : "No driver records found"
                        }
                        description={
                          driverOnly
                            ? undefined
                            : "No drivers match this branch/search. Clear filters or add a driver."
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
                {loadError && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <OperationsEmptyState
                        title="Couldn't load drivers"
                        description="Something went wrong fetching the register."
                      />
                      <Box display="flex" justifyContent="center" pb={2}>
                        <Button variant="outlined" sx={standardGhostBtnSx} onClick={() => fetchAllDriversList(currentPage)}>
                          Retry
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Shared by every tier — hoisted out of TableContainer (it renders
            through a portal regardless of DOM position) so the card tier,
            which has no TableContainer, can open the same menu. */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setSelectedItem(null);
          }}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              if (selectedItem) {
                handleOpenDetails(selectedItem);
              }
            }}
          >
            Open profile
          </MenuItem>
          {!driverOnly && driverStatus === "AVAILABLE" && (
            <MenuItem
              sx={{ color: ifasColors.deepOrange, fontWeight: 800 }}
              onClick={() => {
                setAnchorEl(null);
                if (selectedItem) {
                  handleAssignDriver(selectedItem);
                }
              }}
            >
              Assign
            </MenuItem>
          )}
          {!driverOnly && ["ASSIGNED", "DISPATCHED"].includes(String(driverStatus || "").toUpperCase()) && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleRemoveDriver();
              }}
            >
              Release
            </MenuItem>
          )}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              handleHistoryClick();
            }}
          >
            History
          </MenuItem>
        </Menu>

        {!driverOnly && (
          <StandardPager
            page={currentPage}
            total={totalElements || 0}
            pageSize={rowsPerPage}
            onPage={setCurrentPage}
          />
        )}
      </Paper>

      <ConfirmDeletePopup
        open={showConfirmDelete}
        onClose={handleCloseConfirmPopup}
        confirmDelete={confirmDelete}
      />
      <VerifiedPopup
        label={popupLabel}
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />
      <AssignDriverPopup
        open={openAssignPopup}
        selectedDriver={selectedItem}
        onClose={() => {
          setOpenAssignPopup(false);
          setSelectedItem(null);
        }}
        handleAssign={handleAssign}
        initialCaseReference={initialCaseReference}
        assignmentPurpose={initialAssignmentPurpose}
        requestReference={initialRequestReference}
        allocationRole={initialAllocationRole}
        allocationSequence={initialAllocationSequence}
      />
    </OperationsPageShell>
  );
};

/**
 * Tier 3 (<=599px) card — one visibleItems row rendered as a card instead of
 * a table row. Always-visible: driver + status + current assignment +
 * vehicle + the status-gated primary action + the same ⋯ menu. "More
 * details" (Collapse) holds the fields the condensed table folds into
 * secondary lines instead: Mobile, Branch, Licence Code, PDP, Licence
 * Expiry — so nothing is lost versus desktop, only re-laid-out.
 */
function DriverCard({
  item,
  driverOnly,
  isFocused,
  expanded,
  onToggleExpanded,
  onOpen,
  onKebab,
  onPrimaryAction,
  registerRef,
}: {
  item: any;
  driverOnly: boolean;
  isFocused: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onOpen: () => void;
  onKebab: (event: React.MouseEvent<HTMLElement>) => void;
  onPrimaryAction: () => void;
  registerRef: (el: HTMLDivElement | null) => void;
}) {
  const status = String(item?.driverStatus || "").toUpperCase();
  const primaryLabel = driverOnly
    ? null
    : status === "AVAILABLE"
      ? "Assign"
      : ["ASSIGNED", "DISPATCHED"].includes(status)
        ? "Release"
        : null;
  const workRef = item.currentCaseNumber || item.currentBodyRemovalNumber || item.currentAllocationNumber;
  const workStatus = item.currentBodyRemovalStatus || item.currentAllocationStatus;

  const fieldLabelSx = {
    fontSize: "10.5px",
    fontWeight: 800,
    letterSpacing: "0.4px",
    textTransform: "uppercase" as const,
    color: ifasColors.muted,
  };
  const fieldValueSx = { fontSize: 13, fontWeight: 700, color: ifasColors.text, mt: 0.25 };

  return (
    <Box
      ref={isFocused ? registerRef : undefined}
      onClick={onOpen}
      sx={{
        border: `1px solid ${ifasColors.border}`,
        borderRadius: "14px",
        bgcolor: isFocused ? ifasColors.softOrange : ifasColors.surface,
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        cursor: "pointer",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: ifasColors.text, fontSize: 15, lineHeight: 1.3 }}>
            {item.firstName} {item.lastName}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: ifasColors.muted, mt: 0.25 }}>
            {item.driverNumber || "N/A"}
          </Typography>
        </Box>
        <StatusDotChip status={item.driverStatus} tone={operationsStatusTone(item.driverStatus)} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
        <Box>
          <Typography sx={fieldLabelSx}>Current Assignment</Typography>
          {workRef ? (
            <>
              <Typography sx={fieldValueSx}>{workRef}</Typography>
              {workStatus && (
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: ifasColors.muted, mt: 0.15 }}>
                  {formatOperationsStatus(workStatus)}
                </Typography>
              )}
            </>
          ) : (
            <Box sx={{ mt: 0.25 }}>
              <DashCell />
            </Box>
          )}
        </Box>
        <Box>
          <Typography sx={fieldLabelSx}>Vehicle</Typography>
          <Typography sx={fieldValueSx}>
            {item.assignedVehicleNumber || item.vehicle || <DashCell />}
          </Typography>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.25,
            pt: 1.25,
            mt: 0.25,
            borderTop: `1px solid ${ifasColors.border}`,
          }}
        >
          <Box>
            <Typography sx={fieldLabelSx}>Mobile</Typography>
            <Typography sx={fieldValueSx}>{item.phoneNumber || "N/A"}</Typography>
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Branch</Typography>
            <Typography sx={fieldValueSx}>{item.branchName || item.branchLocation || "Unassigned"}</Typography>
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Licence Code</Typography>
            <Typography sx={fieldValueSx}>{item.licenseCode || "N/A"}</Typography>
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>PDP</Typography>
            <Typography sx={{ ...fieldValueSx, color: item.hasPdp ? STANDARD_TONES.green.color : ifasColors.muted }}>
              {item.hasPdp ? "Yes" : "No"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Licence Expiry</Typography>
            <Typography sx={fieldValueSx}>{item.licenseExpiryDate || "N/A"}</Typography>
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pt: 0.25 }}>
        <Button
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpanded();
          }}
          sx={{ textTransform: "none", fontWeight: 800, fontSize: 12.5, color: ifasColors.orange, minHeight: 44, px: 1 }}
        >
          {expanded ? "Less" : "More details"}
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {primaryLabel && (
            <Button
              variant={primaryLabel === "Assign" ? "contained" : "outlined"}
              onClick={(event) => {
                event.stopPropagation();
                onPrimaryAction();
              }}
              sx={{
                ...(primaryLabel === "Assign" ? standardPrimaryBtnSx : standardGhostBtnSx),
                minHeight: 44,
                px: 1.5,
              }}
            >
              {primaryLabel}
            </Button>
          )}
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onKebab(event);
            }}
            sx={{ width: 44, height: 44 }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default Drivers;
